import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Check, X, Eye, MessageSquare, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/i18n/LanguageContext';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { useState } from 'react';

interface QueueItem {
  id: string;
  user_id: string;
  recipient_id: string;
  generated_message: string;
  generated_template: string;
  status: string;
  scheduled_send_at: string;
  created_at: string;
  recipient?: {
    recipient_name: string;
    relationship: string;
  };
}

export function PendingApprovals() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const queryClient = useQueryClient();
  const [previewItem, setPreviewItem] = useState<QueueItem | null>(null);

  const content = {
    en: {
      noPending: 'No pending approvals',
      noPendingDesc: 'Messages awaiting your approval will appear here 24 hours before scheduled send',
      scheduledFor: 'Scheduled for',
      timeLeft: 'Time left to review',
      approve: 'Approve & Send',
      skip: 'Skip This One',
      preview: 'Preview',
      approving: 'Approving...',
      to: 'To',
      template: 'Template',
      previewTitle: 'Preview Message',
      previewDesc: 'Review the AI-generated message before approving',
    },
    es: {
      noPending: 'No hay aprobaciones pendientes',
      noPendingDesc: 'Los mensajes que esperan tu aprobación aparecerán aquí 24 horas antes del envío programado',
      scheduledFor: 'Programado para',
      timeLeft: 'Tiempo para revisar',
      approve: 'Aprobar y Enviar',
      skip: 'Omitir Este',
      preview: 'Vista Previa',
      approving: 'Aprobando...',
      to: 'Para',
      template: 'Plantilla',
      previewTitle: 'Vista Previa del Mensaje',
      previewDesc: 'Revisa el mensaje generado por IA antes de aprobar',
    },
  };

  const t = content[language as keyof typeof content] || content.en;

  const { data: queueItems, isLoading } = useQuery({
    queryKey: ['auto-compose-queue', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('auto_compose_queue')
        .select(`
          *,
          recipient:auto_compose_recipients(recipient_name, relationship)
        `)
        .eq('status', 'pending_approval')
        .order('scheduled_send_at', { ascending: true });

      if (error) throw error;
      return data as QueueItem[];
    },
    enabled: !!user,
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'approved' | 'skipped' }) => {
      const { error } = await supabase
        .from('auto_compose_queue')
        .update({ 
          status, 
          user_response_at: new Date().toISOString() 
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: ['auto-compose-queue'] });
      const approvedMsg = ({ en: 'Message approved!', es: '¡Mensaje aprobado!', fr: 'Message approuvé !', pt: 'Mensagem aprovada!', ar: 'تمت الموافقة على الرسالة!', ja: 'メッセージ承認済み！', ko: '메시지 승인됨!', id: 'Pesan disetujui!' }[language]) || 'Message approved!';
      const skippedMsg = ({ en: 'Message skipped', es: 'Mensaje omitido', fr: 'Message ignoré', pt: 'Mensagem ignorada', ar: 'تم تخطي الرسالة', ja: 'メッセージをスキップ', ko: '메시지 건너뜀', id: 'Pesan dilewati' }[language]) || 'Message skipped';
      toast.success(status === 'approved' ? approvedMsg : skippedMsg);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update status');
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-32 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!queueItems?.length) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-12 text-center">
          <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">{t.noPending}</h3>
          <p className="text-muted-foreground">{t.noPendingDesc}</p>
        </CardContent>
      </Card>
    );
  }

  const templateLabels: Record<string, { en: string; es: string }> = {
    classic: { en: 'Classic Elegance', es: 'Elegancia Clásica' },
    modern: { en: 'Modern Warmth', es: 'Calidez Moderna' },
    bold: { en: 'Bold Statement', es: 'Declaración Audaz' },
    journal: { en: 'Intimate Journal', es: 'Diario Íntimo' },
  };

  return (
    <>
      <div className="space-y-4">
        <AnimatePresence>
          {queueItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="border-primary/20">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-primary" />
                      {t.to}: {item.recipient?.recipient_name || 'Unknown'}
                    </CardTitle>
                    <Badge variant="outline">
                      {templateLabels[item.generated_template]?.[language] || item.generated_template}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pb-2">
                  <p className="text-muted-foreground line-clamp-3 mb-4">
                    {item.generated_message}
                  </p>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      {t.scheduledFor}: {format(new Date(item.scheduled_send_at), 'MMM d, h:mm a')}
                    </div>
                    <div className="flex items-center gap-1 text-amber-600">
                      <Clock className="h-4 w-4" />
                      {t.timeLeft}: {formatDistanceToNow(new Date(item.scheduled_send_at))}
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-4 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPreviewItem(item)}
                    className="gap-1"
                  >
                    <Eye className="h-4 w-4" />
                    {t.preview}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatusMutation.mutate({ id: item.id, status: 'skipped' })}
                    className="gap-1 text-muted-foreground"
                  >
                    <X className="h-4 w-4" />
                    {t.skip}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => updateStatusMutation.mutate({ id: item.id, status: 'approved' })}
                    disabled={updateStatusMutation.isPending}
                    className="gap-1 ml-auto"
                  >
                    <Check className="h-4 w-4" />
                    {updateStatusMutation.isPending ? t.approving : t.approve}
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Preview Dialog */}
      <Dialog open={!!previewItem} onOpenChange={() => setPreviewItem(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t.previewTitle}</DialogTitle>
            <DialogDescription>{t.previewDesc}</DialogDescription>
          </DialogHeader>
          {previewItem && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t.to}: <span className="font-medium text-foreground">{previewItem.recipient?.recipient_name}</span>
                </span>
                <Badge>
                  {templateLabels[previewItem.generated_template]?.[language]}
                </Badge>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="whitespace-pre-wrap leading-relaxed">
                  {previewItem.generated_message}
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    updateStatusMutation.mutate({ id: previewItem.id, status: 'skipped' });
                    setPreviewItem(null);
                  }}
                >
                  <X className="h-4 w-4 mr-2" />
                  {t.skip}
                </Button>
                <Button
                  onClick={() => {
                    updateStatusMutation.mutate({ id: previewItem.id, status: 'approved' });
                    setPreviewItem(null);
                  }}
                >
                  <Check className="h-4 w-4 mr-2" />
                  {t.approve}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
